import assistantStore from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import { KW } from './ui'

/**
 * The notice a case manager or specialist reads before their first question. It takes the place
 * of the chat until it is closed; the server remembers that per user, so it is shown once and not
 * once per browser. The text comes from the server (it names the organisation's own retention
 * period).
 */
const PrivacyNotice = () => {
  const session = useAssistantSession()
  const notice = assistantStore.privacyNotice
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    buttonRef.current?.focus()
    return () => {
      mountedRef.current = false
    }
  }, [])

  if (!notice) {
    return null
  }

  const acknowledge = async () => {
    setSaving(true)
    setFailed(false)
    const saved = await assistantStore.acknowledgePrivacyNotice()
    if (!mountedRef.current) {
      return
    }
    setSaving(false)
    if (saved) {
      // The notice is gone, and with it the button that had the focus.
      session.requestComposerFocus()
    } else {
      setFailed(true)
    }
  }

  return (
    <section
      className="kw-scroll min-h-0 flex-1 overflow-y-auto px-5 py-6"
      aria-labelledby="kw-privacy-notice-title"
    >
      <h2
        id="kw-privacy-notice-title"
        className="mb-3 text-base font-extrabold"
        style={{ color: KW.ink }}
      >
        {notice.title}
      </h2>
      {notice.lines.map(line => (
        <p
          key={line}
          className="mb-3 text-[13px] leading-relaxed"
          style={{ color: KW.ink }}
        >
          {line}
        </p>
      ))}
      <button
        ref={buttonRef}
        type="button"
        className="kw-history-btn mt-2"
        disabled={saving}
        onClick={acknowledge}
      >
        {saving ? 'Saving…' : 'I understand'}
      </button>
      {failed && (
        <div
          role="alert"
          className="mt-3 text-[13px]"
          style={{ color: KW.red }}
        >
          This could not be saved. Please try again.
        </div>
      )}
    </section>
  )
}

export default observer(PrivacyNotice)
